// @ts-check
import { computed, ref, watch } from 'vue';

import {
  createBaseParties,
  createNewParty,
} from '@/store/modules/bulkSend/factories/parties';
import { SavedParties } from '@/store/modules/bulkSend/models';

/** @typedef {import('@/store/modules/bulkSend/models/Parties').TUpdateParty} TUpdateParty */
/** @typedef {import('@/store/modules/bulkSend/models/Parties').TUpdateParties} TUpdateParties */
/** @typedef {import('@/store/modules/bulkSend/models/Parties').TParties} TParties */
/** @typedef {import('@/store/modules/bulkSend/models/Templates').TTemplates} TTemplates */

/**
 * @param {import('vue').Ref<TUpdateParties>} parties
 * @param {import('vue').Ref<TTemplates>} templates
 */
export function useBulkSendRolesStatus(parties, templates) {
  const assignedRoles = computed(() =>
    parties.value.flatMap(party => party.document_party_ids));

  const unassignedRoles = computed(() =>
      templates.value.flatMap(template => template.parties.filter(party => party.role === 'signer')).length - assignedRoles.value.length);

  const templatesWithRoles = computed(() =>
    templates.value.filter(template => template.parties.filter(party => party.role === 'signer').length));

  const templatesWithoutRoles = computed(() =>
    templates.value.filter(template => !template.parties.filter(party => party.role === 'signer').length));

  const templatesWithCopyOnlyRoles = computed(() =>
      templates.value.filter(template => template.parties.filter(party => party.role === 'copy').length));

  const canRemoveParty = computed(() => parties.value.length > 1);


  /** @param {TUpdateParty} party */
  function isCopy(party) {
    return party.role === 'copy';
  }

  /**
   * @param {TUpdateParty} party
   * @param {number} id
   */
  function hasRole(party, id) {
    return party.document_party_ids.includes(id);
  }

  /**
   * @param {TUpdateParty} party
   * @param {number} id
   */
  function availableRole(party, id) {
    return hasRole(party, id) || !assignedRoles.value.includes(id);
  }

  /**
   * @param {number} id
   */
  function assignedTo(id) {
    return parties.value.findIndex(party =>
      party.document_party_ids.includes(id));
  }

  return {
    templatesWithRoles,
    templatesWithoutRoles,
    canRemoveParty,
    isCopy,
    hasRole,
    availableRole,
    assignedTo,
    unassignedRoles,
    templatesWithCopyOnlyRoles,
  };
}

/**
 * Creates the initial parties to assign roles to
 * updates parties when templates change
 *
 * @param {import('vue').Ref<TParties>} currentParties
 * @param {import('vue').Ref<TTemplates>} templates
 */
export function useBulkSendRolesParties(currentParties, templates) {
  /** @type {import('vue').Ref<TUpdateParties>} parties */
  const parties = ref(null);

  /**
   * Ensures the minimum required parties exist
   */
  function setParties() {
    // if we are updating we will have parties if not parse saved parties
    const startingParties =
      parties.value || SavedParties.parse(currentParties.value);

    const templateRoleIds = templates.value.flatMap(template =>
        template.parties.filter(party => party.role === 'signer').map(party => party.id));

    // prune to remove no longer needed parties
    const prunedParties = startingParties
      .map((party) => {
        const extantIds = party.document_party_ids.filter(id =>
          templateRoleIds.includes(id));

        // mark "base" parties and parties that had roles but no longer do to be pruned
        if (
          (('base' in party && party.base) || party.document_party_ids.length) &&
          !extantIds.length
        ) {
          return undefined;
        }

        return { ...party, document_party_ids: extantIds };
      })
      .filter(party => !!party);

    // parties with roles assigned
    const inuseParties = prunedParties.filter(party => party.document_party_ids.length).length;

    // the maximum number of parties on a template, or at least 1
    const minParties = Math.max(1, ...templates.value.map(template => template.parties.filter(party => party.role === 'signer').length));

    // parties to add so we have the minimum required to assign all roles
    const baseParties = createBaseParties(minParties - inuseParties);

    // combine the existing parties with new base parties and sort to put "copy"'s last
    parties.value = [...prunedParties, ...baseParties].sort((a, b) => {
      if (a.role === 'copy') return 1;
      if (b.role === 'copy') return -1;
      return 0;
    });
  }

  // refresh parties if templates change
  watch(
    templates,
    () => setParties(),
    { immediate: true },
  );

  return parties;
}

/**
 * @param {import('vue').Ref<TUpdateParties>} parties
 * @param {import('vue').Ref<TTemplates>} templates
 */
export function useBulkSendRoles(parties, templates) {
  const { hasRole, canRemoveParty } = useBulkSendRolesStatus(
    parties,
    templates,
  );

  const templateRoles = computed(() =>
    templates.value.map(template =>
      template.parties.filter(party => party.role === 'signer').map(party => party.id)));

  /**
   * @param {TUpdateParty} party
   * @param {number} id
   */
  function deselectRole(party, id) {
    const roleIndex = party.document_party_ids.indexOf(id);

    if (roleIndex > -1) {
      party.document_party_ids.splice(roleIndex, 1);
    }
  }

  /**
   * @param {number} index - party index
   * @param {number} id
   */
  function selectRole(index, id) {
    const party = parties.value[index];

    if (hasRole(party, id)) {
      deselectRole(party, id);
    } else {
      parties.value.forEach(forParty => deselectRole(forParty, id));
      const roleIds = templateRoles.value.find(roles => roles.includes(id));
      roleIds.forEach(roleId => deselectRole(party, roleId));
      party.document_party_ids.push(id);
      if ('base' in party) {
        party.base = false;
      }
    }
  }

  function addParty() {
    parties.value.push(createNewParty());
  }

  /** @param {number} index - party index */
  function removeParty(index) {
    if (canRemoveParty) {
      parties.value.splice(index, 1);
    }
  }

  /** @param {number} index - party index */
  function togglePartyRole(index) {
    const party = parties.value[index];

    if (party.role === 'signer') {
      party.role = 'copy';
      party.document_party_ids = [];
    } else {
      party.role = 'signer';
    }
  }

  return {
    selectRole,
    deselectRole,
    addParty,
    removeParty,
    togglePartyRole,
  };
}
